<article
  class="quote-card"
  [class.quote-card--previous]="readonlyMode()"
  [class.quote-card--pending]="isPending()"
  [attr.aria-label]="'Estimate from ' + vendorName()"
  [attr.data-testid]="
    isPending() ? 'pending-vendor-card' : 'quoted-vendor-card'
  "
>
  @if (isPending() && !isCancelled()) {
    <!-- Pending quote: show only vendor name and message -->
    <div class="quote-card__header">
      <span
        class="quote-card__vendor-name"
        [class.quote-card__vendor-name--rejected]="
          pendingStatusLabel() === 'Cancelled'
        "
        data-testid="vendor-name"
        >{{ vendorName() }}</span
      >
    </div>
    @if (pendingStatusLabel()) {
      <p
        class="quote-card__pending-message quote-card__pending-message--with-status"
        data-testid="pending-quote-message"
      >
        <span class="quote-card__pending-status">{{
          pendingStatusLabel()
        }}</span>
        <span class="quote-card__pending-cancel-message">{{
          pendingMessage()
        }}</span>
      </p>
    } @else {
      <p
        class="quote-card__pending-message"
        data-testid="pending-quote-message"
      >
        {{ pendingMessage() }}
      </p>
    }
  } @else if (isCancelled()) {
    <!-- Cancelled quote: vendor name + cancellation date + reason -->
    <div class="quote-card__header">
      <span
        class="quote-card__vendor-name quote-card__vendor-name--rejected"
        data-testid="vendor-name"
        >{{ vendorName() }}</span
      >
    </div>
    <div class="quote-card__cancelled" data-testid="cancelled-vendor-card">
      <p class="quote-card__cancelled-row">
        @if (cancellationReasonLabel()) {
          <span class="quote-card__cancelled-reason-group">
            <span class="quote-card__cancelled-caption">Cancelled</span>
            <span
              class="quote-card__cancelled-value"
              data-testid="cancelled-reason"
              >{{ cancellationReasonLabel() }}</span
            >
          </span>
        }
      </p>
    </div>
  } @else {
    <div class="quote-card__header">
      <span
        class="quote-card__vendor-name"
        [class.quote-card__vendor-name--rejected]="statusLabel() === 'Rejected'"
        data-testid="vendor-name"
        >{{ vendorName() }}</span
      >
      @if (!readonlyMode()) {
        @if (this.shouldShowDetailsBtn()) {
          <button
            type="button"
            class="quote-card__view-details"
            data-testid="view-details"
            (click)="viewDetails.emit(estimate())"
          >
            View Details
          </button>
        } @else if (statusLabel() === "Cancelled") {
          <span
            class="quote-card__status-indicator quote-card__status-indicator--cancelled"
          >
            {{ statusLabel() }}
          </span>
        }
      }
    </div>

    @if (
      !readonlyMode() &&
      statusLabel() !== "Pending quote estimates" &&
      statusLabel() !== "Cancelled" &&
      statusLabel() !== "Rejected"
    ) {
      <p class="quote-card__deadline" data-testid="response-required-by">
        Expires on: &nbsp;<strong>{{ responseDeadline() }}</strong>
      </p>
    }

    @if (
      rows().length &&
      statusLabel() !== "Pending quote estimates" &&
      statusLabel() !== "Rejected"
    ) {
      <div class="quote-card__table-wrapper">
        <table class="quote-card__table" aria-label="Quote breakdown">
          <thead>
            <tr>
              <th scope="col">Job Type</th>
              <th scope="col">Delivery Date</th>
              <th scope="col">
                {{
                  estimate().jobStatus === "AWAITING_VENDOR_APPROVAL"
                    ? "Estimated Cost"
                    : "Final Cost"
                }}
              </th>
              <th scope="col">Important Considerations</th>
              @if (!readonlyMode()) {
                <th scope="col"></th>
              }
            </tr>
          </thead>
          <tbody>
            @for (row of rows(); track row.jobType) {
              <tr>
                <td data-label="Job Type">
                  <span class="quote-card__job-type">
                    <svg
                      class="quote-card__icon"
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M10.1839 18.3886C9.03114 18.3886 7.94781 18.1698 6.93392 17.7323C5.92003 17.2948 5.03809 16.7011 4.28809 15.9511C3.53809 15.2011 2.94434 14.3191 2.50684 13.3052C2.06934 12.2913 1.85059 11.208 1.85059 10.0552C1.85059 8.90245 2.06934 7.81912 2.50684 6.80523C2.94434 5.79134 3.53809 4.90939 4.28809 4.15939C5.03809 3.40939 5.92003 2.81564 6.93392 2.37814C7.94781 1.94064 9.03114 1.72189 10.1839 1.72189C11.3367 1.72189 12.42 1.94064 13.4339 2.37814C14.4478 2.81564 15.3298 3.40939 16.0798 4.15939C16.8298 4.90939 17.4235 5.79134 17.861 6.80523C18.2985 7.81912 18.5173 8.90245 18.5173 10.0552C18.5173 10.4302 18.4964 10.7983 18.4548 11.1594C18.4131 11.5205 18.3436 11.8747 18.2464 12.2219C18.052 11.9997 17.8263 11.8122 17.5693 11.6594C17.3124 11.5066 17.0311 11.4024 16.7256 11.3469C16.7673 11.1386 16.7985 10.9268 16.8193 10.7115C16.8402 10.4962 16.8506 10.2774 16.8506 10.0552C16.8506 8.19412 16.2048 6.61773 14.9131 5.32606C13.6214 4.03439 12.045 3.38856 10.1839 3.38856C8.32281 3.38856 6.74642 4.03439 5.45475 5.32606C4.16309 6.61773 3.51725 8.19412 3.51725 10.0552C3.51725 11.9163 4.16309 13.4927 5.45475 14.7844C6.74642 16.0761 8.32281 16.7219 10.1839 16.7219C10.8923 16.7219 11.5693 16.6177 12.2152 16.4094C12.861 16.2011 13.4548 15.9094 13.9964 15.5344C14.1631 15.7705 14.3679 15.9788 14.611 16.1594C14.8541 16.3399 15.1145 16.4788 15.3923 16.5761C14.6839 17.1455 13.8888 17.5899 13.0068 17.9094C12.1249 18.2288 11.1839 18.3886 10.1839 18.3886ZM16.2256 15.0552C15.9339 15.0552 15.6874 14.9545 15.486 14.7531C15.2846 14.5518 15.1839 14.3052 15.1839 14.0136C15.1839 13.7219 15.2846 13.4754 15.486 13.274C15.6874 13.0726 15.9339 12.9719 16.2256 12.9719C16.5173 12.9719 16.7638 13.0726 16.9652 13.274C17.1666 13.4754 17.2673 13.7219 17.2673 14.0136C17.2673 14.3052 17.1666 14.5518 16.9652 14.7531C16.7638 14.9545 16.5173 15.0552 16.2256 15.0552ZM12.9339 13.9719L9.35059 10.3886V5.88856H11.0173V9.72189L14.1006 12.8052L12.9339 13.9719Z"
                        fill="#333333"
                      />
                    </svg>
                    <p>{{ row.jobType }}</p>
                  </span>
                </td>
                <td data-label="Delivery Date">{{ row.deliveryDate }}</td>
                <td data-label="Final Cost">
                  {{ row.estimatedCost | formatCurrency: row.currency }}
                </td>
                <td data-label="Important Considerations">
                  @if (row.consideration) {
                    <span class="quote-card__consideration">
                      @if (row.considerationIcon === "lowest") {
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M9.15 20C8.9 20 8.65 19.95 8.4 19.85C8.15 19.75 7.925 19.6 7.725 19.4L0.575 12.25C0.375 12.05 0.229167 11.8292 0.1375 11.5875C0.0458333 11.3458 0 11.1 0 10.85C0 10.6 0.0458333 10.35 0.1375 10.1C0.229167 9.85 0.375 9.625 0.575 9.425L9.375 0.6C9.55833 0.416667 9.775 0.270833 10.025 0.1625C10.275 0.0541667 10.5333 0 10.8 0H17.975C18.525 0 18.9958 0.195833 19.3875 0.5875C19.7792 0.979167 19.975 1.45 19.975 2V9.175C19.975 9.44167 19.925 9.69583 19.825 9.9375C19.725 10.1792 19.5833 10.3917 19.4 10.575L10.575 19.4C10.375 19.6 10.15 19.75 9.9 19.85C9.65 19.95 9.4 20 9.15 20ZM9.15 18L17.975 9.15V2H10.825L2 10.85L9.15 18ZM15.475 6C15.8917 6 16.2458 5.85417 16.5375 5.5625C16.8292 5.27083 16.975 4.91667 16.975 4.5C16.975 4.08333 16.8292 3.72917 16.5375 3.4375C16.2458 3.14583 15.8917 3 15.475 3C15.0583 3 14.7042 3.14583 14.4125 3.4375C14.1208 3.72917 13.975 4.08333 13.975 4.5C13.975 4.91667 14.1208 5.27083 14.4125 5.5625C14.7042 5.85417 15.0583 6 15.475 6Z"
                            fill="#333333"
                          />
                        </svg>
                      } @else if (row.considerationIcon === "warning") {
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="20"
                          viewBox="0 0 18 20"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M6.7 16.7L5.3 15.3L7.6 13L5.3 10.7L6.7 9.3L9 11.6L11.3 9.3L12.7 10.7L10.4 13L12.7 15.3L11.3 16.7L9 14.4L6.7 16.7ZM2 20C1.45 20 0.979167 19.8042 0.5875 19.4125C0.195833 19.0208 0 18.55 0 18V4C0 3.45 0.195833 2.97917 0.5875 2.5875C0.979167 2.19583 1.45 2 2 2H3V0H5V2H13V0H15V2H16C16.55 2 17.0208 2.19583 17.4125 2.5875C17.8042 2.97917 18 3.45 18 4V18C18 18.55 17.8042 19.0208 17.4125 19.4125C17.0208 19.8042 16.55 20 16 20H2ZM2 18H16V8H2V18ZM2 6H16V4H2V6Z"
                            fill="#333333"
                          />
                        </svg>
                      }
                      <strong>{{ row.consideration }}</strong>
                    </span>
                  } @else {
                    <span class="quote-card__no-consideration">--</span>
                  }
                </td>
                @if (!readonlyMode()) {
                  @if (
                    estimate().priceMax &&
                    estimate().jobStatus === "AWAITING_VENDOR_SELECTION"
                  ) {
                    <td>
                      <app-vendor-select-btn
                        [order]="order()"
                        [estimate]="estimate()"
                        [hasLowestRate]="isLowestStandard()"
                        (quoteConfirmed)="quoteConfirmed.emit($event)"
                      />
                    </td>
                  } @else if (
                    estimate().jobStatus === "AWAITING_VENDOR_APPROVAL" &&
                    hasVendorSelection()
                  ) {
                    <td data-label="Delivery Date" class="awaiting_final_quote">
                      Awaiting Quote
                    </td>
                  } @else {
                    <td></td>
                  }
                }
              </tr>
            }
          </tbody>
        </table>
      </div>
    } @else if (statusLabel() === "Rejected") {
      <div class="quote-card__rejected" data-testid="rejected-vendor-card">
        <div class="quote-card__rejected-meta">
          <p class="quote-card__rejected-row quote-card__rejected-row--top">
            <span class="quote-card__rejected-label">Rejected</span>
            <span
              class="quote-card__rejected-reason"
              data-testid="rejected-reason"
            >
              {{ rejectedReason() }}
            </span>
            <span class="quote-card__rejected-date-group">
              <span class="quote-card__rejected-caption">Job rejected on</span>
              <span
                class="quote-card__rejected-value"
                data-testid="rejection-date"
                >{{ rejectionDate() }}</span
              >
            </span>
          </p>
          @if (rejectedDetails()) {
            <p class="quote-card__rejected-row">
              <span
                class="quote-card__rejected-value"
                data-testid="rejected-details"
                >{{ rejectedDetails() }}</span
              >
            </p>
          }
        </div>
      </div>
    } @else {
      <div class="quote-card__awaiting">
        <span
          class="quote-card__status-indicator"
          [class]="'quote-card__status-indicator--' + statusClass()"
        >
          {{ statusLabel() }}
        </span>
      </div>
    }
  }
</article>
